import * as React from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import ImageIcon from "@mui/icons-material/Image";
import CheckIcon from "@mui/icons-material/Check";

import NewRadioSelector from "../../components/NewRadioSelector";
import { useSearch } from "../../contexts/searchContext";
import { GenerateCharacteristicsForSelector } from "./CharacteristicsGenerator";

function CharacteristicsSelector() {
  const {
    enumMap,
    setEnumMap,
    searchString,
    setSearchString,
    selectedCharacteristics,
    datasets,
    datasetObjects,
    datasetsLoading,
    datasetsError,
    selectedDatasets,
    setSelectedDatasets,
    visibleCharacteristics
  } = useSearch();

  // Always return common characteristics from shared constants
  const characteristicsSchema = GenerateCharacteristicsForSelector();

  // Filter characteristics by visibility settings (admin can toggle these)
  const characteristics = React.useMemo(() =>
    selectedCharacteristics.filter(char =>
      visibleCharacteristics[char] !== false
    ),
    [selectedCharacteristics, visibleCharacteristics]
  );

  const handleCharacteristicEvent = React.useCallback((event, enumType, enumValue) => {
    const newEnumMap = new Map(enumMap);

    if (enumValue === null || enumValue === undefined) {
      newEnumMap.delete(enumType);
    } else {
      newEnumMap.set(enumType, enumValue);
    }

    setEnumMap(newEnumMap);
  }, [enumMap, setEnumMap]);

  const handleDatasetToggle = React.useCallback((datasetId) => {
    setSelectedDatasets(prev => {
      if (prev.includes(datasetId)) {
        // Uncheck - but prevent removing if it's the last one
        if (prev.length === 1) {
          return prev; // Keep at least one dataset selected
        }
        return prev.filter(id => id !== datasetId);
      } else {
        // Check - add to array
        return [...prev, datasetId];
      }
    });
  }, [setSelectedDatasets]);

  const handleSearchChange = React.useCallback((event) => {
    const value = event.target.value;
    setSearchString(value);
  }, [setSearchString]);

  const handleClearSearch = React.useCallback(() => {
    setSearchString("");
  }, [setSearchString]);

  const getInitialValue = (characteristic) => {
    return enumMap.has(characteristic) ? enumMap.get(characteristic) : null;
  };

  return (
    <Box sx={{ p: 1 }}>
      <Stack spacing={1}>
        {/* Search Field */}
        <TextField
          label="Search by Name or Headline"
          variant="outlined"
          value={searchString}
          onChange={handleSearchChange}
          size="small"
          fullWidth
          sx={{
            maxWidth: 400,
            '& .MuiOutlinedInput-root': {
              transition: 'all 0.2s',
              '&:hover': {
                boxShadow: 1,
              },
              '&.Mui-focused': {
                boxShadow: 2,
              }
            }
          }}
          InputProps={{
            endAdornment: searchString && (
              <InputAdornment position="end">
                <IconButton
                  aria-label="clear search"
                  onClick={handleClearSearch}
                  edge="end"
                  size="small"
                  sx={{
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    }
                  }}
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Divider />

        {/* Dataset Icons */}
        <Box>
          {datasetsLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Loading datasets...
              </Typography>
            </Box>
          )}

          {datasetsError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              Failed to load datasets: {datasetsError}
            </Alert>
          )}

          {!datasetsLoading && !datasetsError && datasets.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No active datasets found
            </Typography>
          )}

          {!datasetsLoading && datasetObjects.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {datasetObjects.map((dataset) => (
                <Box
                  key={dataset.dataset_id}
                  onClick={() => handleDatasetToggle(dataset.dataset_id)}
                  sx={{
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'scale(1.05)',
                    }
                  }}
                >
                  <Avatar
                    src={dataset.icon_url}
                    variant="rounded"
                    sx={{
                      width: 50,
                      height: 50,
                      bgcolor: 'grey.100',
                      border: selectedDatasets.includes(dataset.dataset_id) ? '3px solid' : '2px solid',
                      borderColor: selectedDatasets.includes(dataset.dataset_id) ? 'primary.main' : 'divider',
                      opacity: selectedDatasets.includes(dataset.dataset_id) ? 1 : 0.5
                    }}
                  >
                    {!dataset.icon_url && (
                      <ImageIcon sx={{ fontSize: 24, color: 'text.disabled' }} />
                    )}
                  </Avatar>
                  {selectedDatasets.includes(dataset.dataset_id) && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -4,
                        right: -4,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <CheckIcon sx={{ fontSize: 14, color: 'white' }} />
                    </Box>
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      textAlign: 'center',
                      mt: 0.5,
                      fontSize: '0.65rem',
                      fontWeight: selectedDatasets.includes(dataset.dataset_id) ? 600 : 400,
                      color: selectedDatasets.includes(dataset.dataset_id) ? 'text.primary' : 'text.secondary'
                    }}
                  >
                    {dataset.dataset_id}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Divider />

        {/* Characteristics Selectors */}
        <Stack spacing={0.75}>
          {characteristics.map((characteristic) => (
            <NewRadioSelector
              key={characteristic}
              enumType={characteristic}
              enumList={characteristicsSchema[characteristic] || []}
              initValue={getInitialValue(characteristic)}
              onEvent={handleCharacteristicEvent}
              selectionRequired={false}
              selectorSize={50}
              disabled={false}
              enumMap={enumMap}
            />
          ))}
        </Stack>
      </Stack>
    </Box>
  );
}

// Memoize to prevent re-renders during search/filtering operations
export default React.memo(CharacteristicsSelector);
